import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import {
  ArrowRight, CheckCircle, Users, Loader2, Building2, Calendar,
  Shield, Sparkles, Target, BarChart3, BookOpen
} from "lucide-react";

const SponsorPortal = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);

  useEffect(() => {
    const fetchPortal = async () => {
      try {
        const res = await fetch(`${API}/sponsor/portal/${slug}`);
        if (res.ok) setPortal(await res.json());
        else setPortal(null);
      } catch (e) { setPortal(null); }
      setLoading(false);
    };
    fetchPortal();
  }, [slug]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("All fields are required"); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/sponsor/portal/${slug}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.token) localStorage.setItem("auth_token", data.token);
        login(data);
        setRegistered(true);
        toast.success("Welcome! Your account has been created.");
        setTimeout(() => navigate("/dashboard"), 1500);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Registration failed");
      }
    } catch (e) { toast.error("Error registering"); }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#39FF14]" />
    </div>
  );

  if (!portal) return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center text-white text-center px-4">
      <div>
        <h1 className="text-2xl font-bold mb-2">Portal Not Found</h1>
        <p className="text-neutral-400 mb-6">This sponsor portal doesn't exist or hasn't been approved yet.</p>
        <Button onClick={() => navigate("/")} variant="outline" className="border-neutral-700 text-white">Go to Homepage</Button>
      </div>
    </div>
  );

  if (registered) return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center text-white text-center px-4">
      <div>
        <CheckCircle className="w-16 h-16 text-[#39FF14] mx-auto mb-4" />
        <h1 className="text-2xl font-bold mb-2">You're All Set!</h1>
        <p className="text-neutral-400">Redirecting to your dashboard...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-950 via-neutral-900 to-neutral-950 text-white">
      {/* Nav */}
      <nav className="border-b border-neutral-800 px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {portal.logo_url && <img src={portal.logo_url} alt={portal.organization_name} className="h-10 w-auto" />}
            <div className="flex items-center gap-2">
              {portal.logo_url && <span className="text-neutral-600">+</span>}
              <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-8 w-auto" />
              <div>
                <p className="text-xs font-semibold text-white leading-tight">My Biz Consulting</p>
                <p className="text-[9px] text-[#39FF14] uppercase tracking-widest">Business Solutions Center</p>
              </div>
            </div>
          </div>
          <Button variant="ghost" onClick={() => navigate("/login")} className="text-sm text-neutral-400 hover:text-white">
            Already have an account? Sign In
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-[#39FF14]/20 text-[#39FF14] border-[#39FF14]/30 mb-6 text-sm">
            {portal.organization_name} Partner Program
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
            {portal.welcome_message || `Welcome to Your Business Development Program`}
          </h1>
          <p className="text-lg text-neutral-400 max-w-2xl mx-auto mb-10">
            Get access to consultant-led strategy, AI-powered business tools, and a professional growth community — sponsored by {portal.organization_name}.
          </p>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">What's Included in Your Program</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: BarChart3, title: "Business Clarity Assessment", desc: "AI-powered diagnostic with personalized growth roadmap" },
              { icon: Target, title: "R.A.P.I.D. Framework", desc: "Consultant-led strategic guidance and accountability" },
              { icon: Sparkles, title: "AI Scaling Tools", desc: "SOPs, business plans, KPI scorecards, and more" },
              { icon: Users, title: "Growth Community", desc: "Networking, mastermind pods, and opportunity marketplace" },
              { icon: BookOpen, title: "Resource Vault", desc: "Templates, guides, training library, and vendor directory" },
              { icon: Shield, title: "Impact Reports", desc: "Professional accountability documentation for your sponsor" }
            ].map((item, i) => (
              <div key={i} className="p-4 bg-neutral-900 rounded-xl border border-neutral-800">
                <item.icon className="w-6 h-6 text-[#39FF14] mb-3" />
                <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-xs text-neutral-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-16 px-4" id="register">
        <div className="max-w-md mx-auto">
          <Card className="bg-neutral-900 border-neutral-800">
            <CardHeader className="text-center">
              <CardTitle className="text-white text-xl">Create Your Account</CardTitle>
              <CardDescription className="text-neutral-400">
                Register to access your {portal.organization_name} sponsored program
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label className="text-neutral-300">Full Name</Label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Your full name" className="bg-neutral-800 border-neutral-700 text-white" required data-testid="sponsor-reg-name" />
                </div>
                <div>
                  <Label className="text-neutral-300">Email</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="you@email.com" className="bg-neutral-800 border-neutral-700 text-white" required data-testid="sponsor-reg-email" />
                </div>
                <div>
                  <Label className="text-neutral-300">Password</Label>
                  <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Min 8 characters" className="bg-neutral-800 border-neutral-700 text-white" required data-testid="sponsor-reg-password" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-[#39FF14] hover:bg-[#32E010] text-black font-semibold h-12" data-testid="sponsor-reg-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                  Get Started
                </Button>
                <p className="text-xs text-center text-neutral-500">
                  By registering, you agree to the My Biz Consulting terms and your sponsor's program requirements.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-neutral-800 py-8 px-4 text-center">
        <div className="flex items-center justify-center gap-3 mb-3">
          <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-6 w-auto brightness-0 invert" />
          <span className="text-xs text-neutral-500">Business Solutions Center</span>
        </div>
        <p className="text-xs text-neutral-600">&copy; {new Date().getFullYear()} My Biz Consulting. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default SponsorPortal;
